import React, { useEffect, useState } from "react";
import { Loader2, ArrowDown, Activity, Award, Gift } from "lucide-react";
import { api } from "../../lib/api";

const FILTERS = [
  { key: "all", label: "All", testId: "log-filter-all", Icon: Activity },
  { key: "earn", label: "Awards", testId: "log-filter-earn", Icon: Award },
  { key: "redeem", label: "Redemptions", testId: "log-filter-redeem", Icon: Gift },
];

const PAGE_SIZE = 20;

const TransactionRow = ({ tx }) => {
  const isEarn = tx.type === "earn";
  const customerName = [tx.customer_first_name, tx.customer_last_name]
    .filter(Boolean)
    .join(" ")
    .trim() || "—";
  const staffName = tx.staff_first_name
    ? [tx.staff_first_name, tx.staff_last_name].filter(Boolean).join(" ").trim()
    : null;
  const amount = tx.amount_spent != null ? `$${tx.amount_spent.toFixed(2)} check` : null;
  const points = `${tx.points_delta > 0 ? "+" : ""}${tx.points_delta.toLocaleString()} pts`;

  return (
    <li
      data-testid={`log-row-${tx.id}`}
      className="flex items-start gap-4 px-5 py-4 border-b border-napoli-gold/15 last:border-0 hover:bg-napoli-espresso-soft/40 transition-colors"
    >
      <div
        aria-hidden
        className={`flex-shrink-0 mt-0.5 w-8 h-8 rounded-full flex items-center justify-center ${
          isEarn ? "bg-napoli-gold/15 text-napoli-gold" : "bg-napoli-oxblood/20 text-napoli-rose"
        }`}
      >
        {isEarn ? <Award size={15} /> : <Gift size={15} />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-body text-[14px] text-napoli-cream leading-snug">
          {staffName ? (
            <>
              <span className="font-semibold">{staffName}</span>{" "}
            </>
          ) : null}
          <span className="font-medium text-napoli-gold">
            {isEarn ? "awarded" : "redeemed"}
          </span>{" "}
          {isEarn ? (
            <span className="font-semibold">{customerName}</span>
          ) : (
            <span className="font-medium italic text-napoli-cream/85">
              {tx.reward_name || "a reward"}
            </span>
          )}
          {isEarn ? (
            <>
              {" "}
              <span className="font-bold text-napoli-gold">{points}</span>
              {amount ? (
                <span className="font-medium text-[13px] text-napoli-cream/60"> · {amount}</span>
              ) : null}
            </>
          ) : (
            <>
              {" "}
              <span className="font-medium text-napoli-cream/70">for</span>{" "}
              <span className="font-semibold">{customerName}</span>{" "}
              <span className="font-bold text-napoli-rose">{points}</span>
            </>
          )}
        </p>
        {tx.notes ? (
          <p className="mt-1 font-body italic font-normal text-[13px] text-napoli-cream/70">
            "{tx.notes}"
          </p>
        ) : null}
        <p className="mt-1.5 font-body font-medium text-[11px] tracking-[0.1em] uppercase text-napoli-cream/50">
          {tx.created_at_relative}
        </p>
      </div>
    </li>
  );
};

const RowSkeleton = () => (
  <li className="flex items-start gap-4 px-5 py-4 border-b border-napoli-gold/10 last:border-0">
    <div className="w-8 h-8 rounded-full bg-napoli-espresso-soft animate-pulse" />
    <div className="flex-1 space-y-2">
      <div className="h-4 w-3/4 bg-napoli-espresso-soft animate-pulse" />
      <div className="h-3 w-1/3 bg-napoli-espresso-soft animate-pulse" />
    </div>
  </li>
);

const ActivityLogPage = () => {
  const [filter, setFilter] = useState("all");
  const [items, setItems] = useState([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [appending, setAppending] = useState(false);
  const [err, setErr] = useState(null);

  const fetchPage = async (replace, currentFilter, currentOffset) => {
    if (replace) setLoading(true);
    else setAppending(true);
    setErr(null);
    try {
      const params = new URLSearchParams({
        limit: String(PAGE_SIZE),
        offset: String(currentOffset),
      });
      if (currentFilter !== "all") params.set("type_filter", currentFilter);
      const { data } = await api.get(`/staff/recent-activity?${params}`);
      setHasMore(!!data.has_more);
      setItems((prev) => (replace ? data.transactions : [...prev, ...data.transactions]));
      setOffset(currentOffset + data.transactions.length);
    } catch (e) {
      setErr("We couldn't load activity. Try again.");
    } finally {
      setLoading(false);
      setAppending(false);
    }
  };

  useEffect(() => {
    fetchPage(true, filter, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  return (
    <section data-testid="activity-log-page" className="px-6 pt-8 pb-12">
      <div className="max-w-3xl mx-auto">
        <p className="font-body font-semibold text-[10px] tracking-[0.3em] uppercase text-napoli-gold">
          Tonight at Napoli
        </p>
        <h1 className="mt-3 font-display font-bold text-[24px] sm:text-[28px] text-napoli-cream leading-tight">
          What the team&rsquo;s doing.
        </h1>
        <p className="mt-2 font-body italic text-[13px] text-napoli-cream/65 max-w-md">
          Awards and redemptions across every server, most recent first.
        </p>

        {/* Filter tabs */}
        <div
          className="mt-7 flex border-b border-napoli-gold/20"
          role="tablist"
          aria-label="Filter activity"
        >
          {FILTERS.map(({ key, label, testId, Icon }) => {
            const active = filter === key;
            return (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={active}
                data-testid={testId}
                onClick={() => setFilter(key)}
                className={[
                  "relative inline-flex items-center gap-2 px-4 py-3 font-body font-semibold text-[11px] tracking-[0.2em] uppercase transition-colors",
                  active ? "text-napoli-gold" : "text-napoli-cream/60 hover:text-napoli-cream/85",
                ].join(" ")}
              >
                <Icon size={14} strokeWidth={1.7} />
                {label}
                {active ? (
                  <span aria-hidden className="absolute -bottom-px left-0 right-0 h-0.5 bg-napoli-gold" />
                ) : null}
              </button>
            );
          })}
        </div>

        {/* List */}
        <ul
          data-testid="activity-log-list"
          className="mt-2 bg-napoli-espresso-soft/40 border border-napoli-gold/15"
        >
          {loading ? (
            <>
              <RowSkeleton /><RowSkeleton /><RowSkeleton /><RowSkeleton />
            </>
          ) : err ? (
            <li data-testid="activity-log-error" className="px-5 py-10 text-center font-body text-[13px] text-napoli-rose">
              {err}
            </li>
          ) : items.length === 0 ? (
            <li
              data-testid="activity-log-empty"
              className="px-5 py-12 text-center"
            >
              <p className="font-display font-bold text-[24px] text-napoli-cream">
                Quiet night.
              </p>
              <p className="mt-2 font-body italic text-[13px] text-napoli-cream/60">
                Be the first to scan a check.
              </p>
            </li>
          ) : (
            items.map((tx) => <TransactionRow key={tx.id} tx={tx} />)
          )}
        </ul>

        {hasMore && !loading ? (
          <div className="mt-6 flex justify-center">
            <button
              type="button"
              data-testid="activity-load-more"
              onClick={() => fetchPage(false, filter, offset)}
              disabled={appending}
              className="inline-flex items-center gap-2 border border-napoli-gold/40 hover:border-napoli-gold text-napoli-cream font-body font-semibold text-[11px] tracking-[0.22em] uppercase px-6 py-3 transition-colors disabled:opacity-50"
            >
              {appending ? (
                <>
                  <Loader2 size={14} className="animate-spin" /> Loading…
                </>
              ) : (
                <>
                  <ArrowDown size={14} /> Load more
                </>
              )}
            </button>
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default ActivityLogPage;
